import { invitationDetailsQuery } from "@/api/invitation";
import { InvitationErrorPage } from "@/components/invitations/InvitationErrorPage";
import { ButtonLink } from "@/components/ui/Link";
import { GivenwellIcon } from "@/icons/givenwell";
import { ApiErrorResponse } from "@common/api/api-types";
import { Box, Flex, Heading, QueryHandler, Span, Text } from "@givenwell/components";
import { styled } from "@givenwell/stitches";
import { colors, fonts } from "@givenwell/theme";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, getRouteApi, Link, redirect, useLocation, useRouter } from "@tanstack/react-router";
import { useEffect } from "react";
import { fallback, object, optional, string } from "valibot";

export const Route = createFileRoute("/invitation/activate")({
  component: Page,
  validateSearch: object({
    token: fallback(optional(string(), ""), ""),
  }),
  beforeLoad: ({ search }) => {
    return {
      token: search.token,
    };
  },
  loader: ({ context }) => {
    if (!context.token) {
      throw redirect({
        to: "/",
      });
    }
  },
});

const route = getRouteApi("/invitation/activate");

function Page() {
  const { token } = route.useSearch();
  const detailsQuery = useQuery(invitationDetailsQuery(token));
  const router = useRouter();
  const location = useLocation();

  useEffect(() => {
    router.preloadRoute({ to: "/new-account", search: { token, kind: "supporter" } });
  }, [router, token]);

  return (
    <QueryHandler
      query={detailsQuery}
      error={({ error: response }) => {
        const apiError = response as unknown as ApiErrorResponse;
        const errorCode = apiError.body?.code?.toString() || apiError?.status?.toString();

        return <InvitationErrorPage code={errorCode} token={token} />;
      }}
      success={({ data: details }) => {
        return (
          <>
            <Flex css={{ flexDir: "column", minHeight: "100%", overflowY: "auto", px: 16, maxW: 500, mx: "auto" }}>
              <Box css={{ flex: "1 0 0px" }} />
              <Flex css={{ flexDir: "column" }}>
                <Box
                  css={{
                    backgroundColor: colors.cream,
                    top: 0,
                    position: "sticky",
                    height: "var(--safe-area-inset-top)",
                    zIndex: 100,
                  }}
                />
                <GivenwellIcon css={{ width: "100%", maxWidth: 180, mx: "auto" }} />

                <Heading
                  css={{
                    lineHeight: 1.25,
                    align: "center",
                    mt: 36,
                    fontScale: "3xl",
                    weight: 500,
                    color: colors.navy,
                    fontFamily: fonts.ftHabit,
                  }}
                >
                  Welcome to Givenwell!
                </Heading>
                <Description css={{ align: "center", textWrap: "balance", mt: 12, mb: 48 }}>
                  {details.supporter_display_name} has invited you to join Givenwell. Activate your account to get
                  started.
                </Description>
                <ButtonLink
                  to="/new-account"
                  search={{
                    token: token,
                    kind: "supporter",
                  }}
                  size="lg"
                  variant="filled"
                  color="pink"
                  css={{
                    w: "100%",
                  }}
                >
                  Activate Account
                </ButtonLink>
              </Flex>
              <Box css={{ flex: "1 0 0px" }} />
              <Text
                css={{
                  paddingBottom: "max(var(--safe-area-inset-bottom), 16px)",
                  mt: 60,
                  align: "center",
                  width: "100%",
                  maxW: 340,
                  mx: "auto",
                }}
              >
                <strong>Already have a Givenwell account?</strong>
                <br />
                <Link
                  to="/merge-account"
                  preload={false}
                  search={{
                    token: token,
                    // @ts-expect-error adding a secret query param so that if the user is not logged in, we can redirect them back to this page from the sign-in page
                    from: location.href,
                    kind: "supporter",
                  }}
                >
                  <Span css={{ textDecoration: "underline", whiteSpace: "nowrap" }}>Click here</Span>
                </Link>{" "}
                to link {details.supporter_display_name} to your existing account.
              </Text>
            </Flex>
          </>
        );
      }}
    />
  );
}

const Description = styled("p", {
  fontScale: "md",
  color: colors.text2,
});
